import { AiAssist } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useParams } from "react-router";

import { TabMenu } from "../TabMenu";

import { useBaseStore } from "@/store/store";

import { useAiLabelRule } from "@/hooks/useAiLabelRule/useAiLabelRule";
import {
  SectionDetailsProps,
  isAssetPageTabItem,
  isEvaluationRoute,
  shouldHideTabMenu,
  useTabs,
} from ".";

function AiLabel() {
  const { t } = useTranslation();
  const { show } = useAiLabelRule();

  if (!show) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 self-end m-4" data-test="ai-label">
      <span className="text-xs font-medium">
        {t("call_context.powered_by_aircall_ai")}
      </span>
      <AiAssist className="size-3" />
    </div>
  );
}

export function SectionDetails({ mobile }: SectionDetailsProps) {
  const { pathname, search } = useLocation();

  const { callId, callType } = useParams();
  const isExpanded = useBaseStore((store) => store.isExpanded);

  const { tabs, disabledTabs } = useTabs();

  if (!isAssetPageTabItem(pathname ?? "")) {
    return <Navigate to={`/calls/${callId}/${callType}/insights${search}`} />;
  }

  const shouldHideTabs = shouldHideTabMenu(pathname);

  const shrink = mobile ? false : isExpanded;

  return (
    <div
      // don't hide this section in mobile screen
      className={`flex flex-1 flex-col bg-card [overflow:initial] [scrollbar-gutter:stable] [transition:opacity_0.25s_ease-in-out,margin_0.3s_ease-in-out,padding_0.3s_ease-in-out] ${
        shrink || mobile
          ? "min-w-0"
          : "min-w-[576px] min-[768px]:max-[1200px]:min-w-[50%]"
      } ${
        shrink
          ? "m-0 opacity-0 rounded-2xl border-none"
          : "m-3 opacity-100 rounded-2xl border border-solid border-border"
      }`}
      data-test="details-section"
    >
      <div
        className="flex flex-1 flex-col overflow-hidden"
        data-test="details-section-inner"
      >
        {!shouldHideTabs && !!tabs.length && (
          <div className="p-4">
            <TabMenu tabs={tabs} disabledTabs={disabledTabs} />
          </div>
        )}
        <div
          className={`flex flex-col gap-6 h-full overflow-auto pb-6 ${
            isEvaluationRoute(pathname) ? "px-0" : "px-6"
          }`}
        >
          <Outlet />
        </div>
        <div className="flex flex-1 flex-col-reverse">
          <AiLabel />
        </div>
      </div>
    </div>
  );
}
